'use client';

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';

import type { ConsoleEntry } from '../types';
import { sanitizeString, sanitizeValue } from '../sanitize';

const MAX_ENTRIES = 100;

type ConsoleLevel = ConsoleEntry['level'];

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).substring(2);
}

function formatArg(arg: unknown): string {
  if (arg instanceof Error) {
    const sanitizedMessage = sanitizeString(arg.message);
    const enumerable = Object.keys(arg);
    if (enumerable.length === 0) return sanitizedMessage;
    const extras: Record<string, unknown> = {};
    for (const key of enumerable) {
      extras[key] = sanitizeValue((arg as unknown as Record<string, unknown>)[key]);
    }
    try {
      return `${sanitizedMessage} ${JSON.stringify(extras)}`;
    } catch {
      return sanitizedMessage;
    }
  }
  if (typeof arg === 'string') {
    return sanitizeString(arg);
  }
  try {
    return JSON.stringify(sanitizeValue(arg));
  } catch {
    return sanitizeString(String(arg));
  }
}

function extractStack(args: unknown[]): string | null {
  for (const arg of args) {
    if (arg instanceof Error && arg.stack) {
      return sanitizeString(arg.stack);
    }
  }
  return null;
}

interface ConsoleLogCaptureContextValue {
  getConsoleLogs: () => ConsoleEntry[];
}

const ConsoleLogCaptureContext =
  createContext<ConsoleLogCaptureContextValue | null>(null);

interface ConsoleLogCaptureProviderProps {
  children: ReactNode;
}

export function ConsoleLogCaptureProvider({
  children,
}: ConsoleLogCaptureProviderProps) {
  const entriesRef = useRef<ConsoleEntry[]>([]);

  useEffect(() => {
    const originalError = console.error;
    const originalWarn = console.warn;
    const originalLog = console.log;

    function createPatchedMethod(
      original: (...args: unknown[]) => void,
      level: ConsoleLevel,
    ) {
      return function (...args: unknown[]) {
        original.apply(console, args);

        const message = args.map(formatArg).join(' ');
        const stack = extractStack(args);

        const entries = entriesRef.current;
        if (entries.length >= MAX_ENTRIES) {
          entries.shift();
        }
        entries.push({
          id: generateId(),
          level,
          message,
          stack,
          timestamp: new Date().toISOString(),
        });
      };
    }

    const patchedError = createPatchedMethod(originalError, 'error');
    const patchedWarn = createPatchedMethod(originalWarn, 'warn');
    const patchedLog = createPatchedMethod(originalLog, 'log');

    console.error = patchedError;
    console.warn = patchedWarn;
    console.log = patchedLog;

    return () => {
      if (console.error === patchedError) console.error = originalError;
      if (console.warn === patchedWarn) console.warn = originalWarn;
      if (console.log === patchedLog) console.log = originalLog;
    };
  }, []);

  const getConsoleLogs = useCallback((): ConsoleEntry[] => {
    return [...entriesRef.current];
  }, []);

  return (
    <ConsoleLogCaptureContext.Provider value={{ getConsoleLogs }}>
      {children}
    </ConsoleLogCaptureContext.Provider>
  );
}

export function useConsoleLogCapture(): ConsoleLogCaptureContextValue {
  const context = useContext(ConsoleLogCaptureContext);
  if (!context) {
    throw new Error(
      'useConsoleLogCapture must be used within a ConsoleLogCaptureProvider',
    );
  }
  return context;
}
